import { gray } from '@kmns-internal/furikake/tokens'
import { Box } from '@mui/material'
import {
  Image,
  ReportQuestion,
  ResponseDatetimeSubTypeEnum,
  ResponseTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { ReactNode } from 'react'
import { convertDecimal } from 'src/exShared/util/numberDecimalPoint/numberDecimalPoint'
import { formatMinutes } from 'src/exShared/util/report/formatMinutes'
import { multipleChoiceColors } from 'src/features/theme/MultipleChoiceColor'
import { ExhaustiveError } from 'src/util/error'
import { formatDatetimeValue } from '../datetimeFormatter.util'

/**
 * 回答済みセルの描画内容。どの回答形式が何として描画されるかを buildAnsweredCell の
 * switch 1 箇所に集約し、描画側は種別で振り分けるだけにする。
 */
export type AnsweredCell =
  | {
      kind: 'value'
      content: ReactNode
      isInvalid?: boolean
      fontColor?: string
    }
  | { kind: 'photo'; images: Image[] }

const valueCell = (
  content: ReactNode,
  options?: { isInvalid?: boolean; fontColor?: string },
): AnsweredCell => ({ kind: 'value', content, ...options })

/** 選択肢の設定色が白やグレーだと読めないので、グレー文字にフォールバックする。 */
const getMultipleChoiceFontColor = (color?: string): string | undefined => {
  if (color === undefined) return undefined
  if (color === '' || color === multipleChoiceColors.default) return gray[6]
  return color
}

/**
 * 回答済みセルの描画内容を回答形式ごとに組み立てる。
 * ResponseTypeEnum に新しい値が増えたら default の ExhaustiveError がコンパイルエラーになる。
 */
export const buildAnsweredCell = (
  question: ReportQuestion,
  responseType: ResponseTypeEnum,
): AnsweredCell => {
  const answer = question.responseAnswer
  switch (responseType) {
    case ResponseTypeEnum.NUMBER: {
      const numberValue = answer?.numberValue
      const value =
        numberValue?.numberValue !== undefined
          ? convertDecimal(
              numberValue.numberValue,
              question.responseNumbers?.[0]?.decimalPoint,
            )
          : ''
      return valueCell(`${value}${question.scale ?? ''}`, {
        isInvalid: numberValue?.isInvalid === 1,
      })
    }
    case ResponseTypeEnum.TEXT:
      return valueCell(answer?.textValue?.textValue)
    case ResponseTypeEnum.DATETIME: {
      const datetimeValue = answer?.datetimeValue
      return valueCell(
        datetimeValue?.datetimeValue
          ? formatDatetimeValue(
              question.responseDatetimes?.[0]?.subType ??
                ResponseDatetimeSubTypeEnum.DATETIME,
              datetimeValue.datetimeValue,
            )
          : '',
        { isInvalid: datetimeValue?.isInvalid === 1 },
      )
    }
    case ResponseTypeEnum.MULTIPLE_CHOICE: {
      const multipleChoice = answer?.multipleChoice
      const isInvalid = multipleChoice?.isInvalid === 1
      return valueCell(multipleChoice?.response, {
        isInvalid,
        fontColor: isInvalid
          ? undefined
          : getMultipleChoiceFontColor(multipleChoice?.color),
      })
    }
    case ResponseTypeEnum.RESPONSE_SET:
      return valueCell(
        (answer?.responseSets ?? []).map(set => set.name).join('、'),
      )
    case ResponseTypeEnum.EMPLOYEE:
      return valueCell(
        (answer?.employees ?? []).map(employee => employee.name).join('、'),
      )
    case ResponseTypeEnum.FORMULA: {
      const formulaValue = answer?.formulaValue
      const value =
        formulaValue?.formulaValue !== undefined
          ? convertDecimal(
              formulaValue.formulaValue,
              question.responseFormulas?.[0]?.decimalPoint,
            )
          : ''
      return valueCell(`${value}${question.scale ?? ''}`, {
        isInvalid: formulaValue?.isInvalid === 1,
      })
    }
    case ResponseTypeEnum.TIME_MEASUREMENT: {
      const timeMeasurement = answer?.timeMeasurementValue
      return valueCell(
        timeMeasurement?.value !== undefined
          ? formatMinutes(timeMeasurement.value)
          : '',
        { isInvalid: timeMeasurement?.isInvalid === true },
      )
    }
    case ResponseTypeEnum.SIGNATURE: {
      const signature = answer?.signatures?.[0]
      return valueCell(
        <Box
          component="img"
          src={signature?.url ?? ''}
          sx={{ aspectRatio: '2', height: 34, display: 'block', mx: 'auto' }}
        />,
      )
    }
    case ResponseTypeEnum.RESULT_IMAGE:
      // 写真はサムネイル + タップでプレビューする専用セル（TableSectionPhotoCell）で描画する。
      return { kind: 'photo', images: answer?.resultImages ?? [] }
    case ResponseTypeEnum.INFORMATION:
    case ResponseTypeEnum.GRID_VARIABLE:
    case ResponseTypeEnum.URL:
    case ResponseTypeEnum.LABEL_CHECK:
      // 表の列の回答形式として選べない形式（columnType.tsx の COLUMN_INPUT_TYPE 外）。
      // 万一データに現れても表では空セルにする。
      return valueCell(undefined)
    default:
      throw new ExhaustiveError(responseType)
  }
}
